import { useRef, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useInView } from 'framer-motion'
import { PageHeader } from '@/components/sections/PageHeader'
import { StatsSection } from '@/components/sections/StatsSection'
import { ValuesSection } from '@/components/sections/ValuesSection'
import { TimelineSection } from '@/components/sections/TimelineSection'
import { CTASection } from '@/components/sections/CTASection'
import { PageMeta } from '@/components/PageMeta'
import { Award, Shield, Leaf, Building2, FileCheck, Download, Building, Play } from 'lucide-react'


const certIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  iso9001: Award,
  iso45001: Shield,
  iso14001: Leaf,
  soa: Building2,
  '231': FileCheck,
}

interface CertificationDoc {
  file: string
  label: string
}

interface Certification {
  key: string
  title: string
  desc: string
  image: string
  documents: CertificationDoc[]
}

// Certificazioni con link download
function CertificationsSectionWithDownloads({ certifications }: { certifications: Certification[] }) {
  const { t } = useTranslation('pages')
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  return (
    <section ref={ref} className="bg-slate-50 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-12 text-center"
        >
          <h2 className="text-3xl font-bold text-slate-900 md:text-4xl">
            {t('certifications.title')}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-lg text-slate-600">
            {t('certifications.subtitle')}
          </p>
        </motion.div>

        {/* Certifications - Horizontal scroll on mobile, grid on desktop */}
        <div className="flex gap-6 overflow-x-auto pb-4 md:grid md:grid-cols-5 md:overflow-visible">
          {certifications.map((cert, index) => {
            const IconComponent = certIcons[cert.key] || Award
            return (
              <motion.div
                key={cert.key}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group min-w-[200px] flex-shrink-0 rounded-2xl bg-white p-6 text-center shadow-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-xl flex flex-col"
              >
                {/* Icon badge */}
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-[#f75e47]/10 text-[#f75e47] transition-colors duration-300 group-hover:bg-[#f75e47] group-hover:text-white">
                  <IconComponent className="h-7 w-7" />
                </div>

                {/* Logo */}
                <div className="mb-4 flex h-20 items-center justify-center">
                  <img
                    src={cert.image}
                    alt={cert.title}
                    className="max-h-16 w-auto object-contain opacity-80 transition-opacity duration-300 group-hover:opacity-100"
                  />
                </div>

                {/* Title */}
                <h3 className="text-sm font-bold text-slate-900 mb-3">
                  {cert.title}
                </h3>

                {/* Download Links */}
                <div className="mt-auto pt-3 border-t border-slate-100">
                  {cert.documents.map((doc, idx) => (
                    <a
                      key={idx}
                      href={doc.file}
                      download
                      className="inline-flex items-center gap-1.5 text-xs text-[#f75e47] hover:underline py-1"
                    >
                      <Download className="h-3 w-3" />
                      {doc.label}
                    </a>
                  ))}
                </div>

                {/* Decorative line on hover */}
                <div className="mx-auto mt-3 h-0.5 w-0 bg-[#f75e47] transition-all duration-300 group-hover:w-12" />
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export function ChiSiamoPage() {
  const { t } = useTranslation('pages')
  const videoPlayerRef = useRef<HTMLVideoElement>(null)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)

  const handlePlayVideo = useCallback(() => {
    if (videoPlayerRef.current) {
      videoPlayerRef.current.play()
      setIsVideoPlaying(true)
    }
  }, [])

  const certifications: Certification[] = [
    { key: 'iso9001', title: 'ISO 9001:2015', desc: t('certifications.iso9001Desc'), image: '/media/certificates/iso-9001.png', documents: [{ file: '/media/certificates/RT05-_ISO-9001_2015-Italiano.pdf', label: t('certifications.downloadPdf') }] },
    { key: 'iso45001', title: 'ISO 45001:2018', desc: t('certifications.iso45001Desc'), image: '/media/certificates/iso-45001.png', documents: [{ file: '/media/certificates/CERTIFICAZIONE-ISO-45001.pdf', label: t('certifications.downloadPdf') }] },
    { key: 'iso14001', title: 'ISO 14001:2015', desc: t('certifications.iso14001Desc'), image: '/media/certificates/iso-14001.png', documents: [{ file: '/media/certificates/ISO-14001-2015.pdf', label: t('certifications.downloadPdf') }] },
    { key: 'soa', title: 'SOA', desc: t('certifications.soaDesc'), image: '/media/images/SOA-2.0.png', documents: [{ file: '/media/documents/att.6303.pdf', label: t('certifications.downloadSoa') }] },
    { key: '231', title: 'Modello 231', desc: t('certifications.231Desc'), image: '/media/logos/logo-231-ok.png', documents: [{ file: '/media/documents/231.PG-All.04_Codice-etico_FG-DATATO.pdf', label: t('certifications.downloadCode') }, { file: '/media/documents/231.M.03-Modulo-segnalazioni-OdV.docx', label: t('certifications.downloadForm') }] },
  ]

  return (
    <>
      <PageMeta title={`${t('chiSiamo.title')} — FG Automazioni`} description={t('chiSiamo.subtitle')} />
      <PageHeader
        title={t('chiSiamo.title')}
        subtitle={t('chiSiamo.subtitle')}
        tag={t('chiSiamo.tag')}
        icon={Building}
        gradient="orange"
        showCta={true}
        ctaText={t('chiSiamo.ctaButton')}
      />

      {/* Il Nostro Percorso - Timeline - sfondo grigio chiaro */}
      <section className="bg-slate-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight md:text-4xl text-center mb-10">{t('aboutJourney.title')}</h2>
          <p className="mx-auto mb-12 max-w-3xl text-lg text-slate-600 leading-relaxed">
            {t('aboutJourney.p1')}
          </p>
          <TimelineSection />
        </div>
      </section>

      {/* I Nostri Valori - sfondo bianco */}
      <ValuesSection />

      {/* Stats - sfondo grigio chiaro */}
      <section className="bg-slate-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <StatsSection />
        </div>
      </section>

      {/* Video Section - Design cinematografico */}
      <section className="relative overflow-hidden bg-white py-20 md:py-28">
        {/* Background decorativo */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-slate-50 opacity-50" />

        <div className="relative mx-auto max-w-7xl px-4 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-[#f75e47]/10 px-4 py-1.5 text-sm font-medium text-[#f75e47] mb-4">
              <Play className="h-4 w-4 fill-current" />
              Video
            </span>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 md:text-4xl lg:text-5xl">
              {t('chiSiamo.videoTitle')}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-600">
              {t('chiSiamo.videoSubtitle')}
            </p>
          </motion.div>

          {/* Video Container con design cinematografico */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative mx-auto max-w-5xl"
          >
            {/* Cornice decorativa */}
            <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-[#f75e47]/20 via-slate-100 to-slate-200 blur-2xl opacity-60" />

            {/* Container video */}
            <div className="relative overflow-hidden rounded-2xl bg-slate-900 shadow-2xl ring-1 ring-slate-900/10">
              {/* Aspect ratio 16:9 wrapper */}
              <div className="relative aspect-video">
                <video
                  ref={videoPlayerRef}
                  src="/media/videos/fg-automazioni-samu-expo-2024.mp4"
                  controls={isVideoPlaying}
                  poster="/media/images/og-image.jpg"
                  className="h-full w-full object-cover"
                  onPlay={() => setIsVideoPlaying(true)}
                  onPause={() => setIsVideoPlaying(false)}
                  onEnded={() => setIsVideoPlaying(false)}
                />

                {/* Play Button Overlay */}
                {!isVideoPlaying && (
                  <motion.button
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 1.2, opacity: 0 }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handlePlayVideo}
                    className="absolute inset-0 flex items-center justify-center bg-slate-900/30 transition-colors duration-300 hover:bg-slate-900/40 cursor-pointer"
                    aria-label="Riproduci video"
                  >
                    <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-[#f75e47] shadow-lg shadow-[#f75e47]/30 md:h-24 md:w-24">
                      {/* Pulse ring */}
                      <span className="absolute inset-0 animate-ping rounded-full bg-[#f75e47]/30" />
                      <Play className="relative h-8 w-8 fill-white text-white ml-1 md:h-10 md:w-10" />
                    </div>
                  </motion.button>
                )}
              </div>

              {/* Glow effect bottom */}
              <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-slate-900/50 to-transparent pointer-events-none" />
            </div>

            {/* Elementi decorativi angoli */}
            <div className="absolute -top-2 -left-2 w-8 h-8 border-t-2 border-l-2 border-[#f75e47]/30 rounded-tl-xl" />
            <div className="absolute -top-2 -right-2 w-8 h-8 border-t-2 border-r-2 border-[#f75e47]/30 rounded-tr-xl" />
            <div className="absolute -bottom-2 -left-2 w-8 h-8 border-b-2 border-l-2 border-[#f75e47]/30 rounded-bl-xl" />
            <div className="absolute -bottom-2 -right-2 w-8 h-8 border-b-2 border-r-2 border-[#f75e47]/30 rounded-br-xl" />
          </motion.div>
        </div>
      </section>

      {/* Certificazioni - Con link download */}
      <CertificationsSectionWithDownloads certifications={certifications} />

      <CTASection />
    </>
  )
}
